import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { Trash2, Edit, Plus, X, ImagePlus, Search, ExternalLink, Loader2 } from 'lucide-react';
import AdminTabs from '../components/AdminTabs';
import { API_BASE_URL } from '../config';
import { compressImage } from '../utils/imageUtils';

const COMPANY_NAME = 'Voltaris Industry & Renewables';
const MAX_IMAGES = 10;

const emptyProject = {
  id: null,
  title: '',
  category: '',
  date: '',
  description: '',
  media_url: '',
  images: [],
};

const AdminProjectsPanel = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [current, setCurrent] = useState(emptyProject);
  const [saving, setSaving] = useState(false);
  const [processingImages, setProcessingImages] = useState(false);
  const [filters, setFilters] = useState({ search: '', category: '', year: '' });
  const fileInputRef = useRef(null);

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/projects`);
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error('Error al cargar los proyectos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProjects(); }, []);

  const categories = useMemo(
    () => [...new Set(projects.map((p) => p.category).filter(Boolean))].sort(),
    [projects]
  );

  const years = useMemo(
    () => [...new Set(projects.map((p) => (p.date || '').slice(0, 4)).filter(Boolean))].sort().reverse(),
    [projects]
  );

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return projects.filter((p) => {
      if (filters.category && p.category !== filters.category) return false;
      if (filters.year && !(p.date || '').startsWith(filters.year)) return false;
      if (!q) return true;
      // Busca en cualquier campo del proyecto
      return [p.title, p.category, p.date, p.description, p.media_url, p.company_name]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q));
    });
  }, [projects, filters]);

  const openModal = (mode, project = emptyProject) => {
    setModalMode(mode);
    setCurrent({ ...emptyProject, ...project, images: [...(project.images || [])] });
    setIsModalOpen(true);
  };

  const handleImagesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const remaining = MAX_IMAGES - current.images.length;
    if (files.length > remaining) {
      toast.error(`Máximo ${MAX_IMAGES} imágenes por proyecto. Se añadirán ${Math.max(remaining, 0)}.`);
    }
    const batch = files.slice(0, remaining);
    if (!batch.length) return;
    setProcessingImages(true);
    try {
      const compressed = [];
      for (const file of batch) {
        compressed.push(await compressImage(file));
      }
      setCurrent((prev) => ({ ...prev, images: [...prev.images, ...compressed] }));
    } catch (err) {
      toast.error('No se pudo procesar alguna de las imágenes');
    } finally {
      setProcessingImages(false);
    }
  };

  const removeImage = (index) => {
    setCurrent((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    if (!current.title.trim() || !current.category.trim() || !current.date || !current.description.trim()) {
      toast.error('Rellena los campos obligatorios: nombre, tipo, fecha y resumen');
      return;
    }
    setSaving(true);
    const url = modalMode === 'create' ? `${API_BASE_URL}/projects` : `${API_BASE_URL}/projects/${current.id}`;
    try {
      const res = await fetch(url, {
        method: modalMode === 'create' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: current.title.trim(),
          company_name: COMPANY_NAME,
          category: current.category.trim(),
          description: current.description.trim(),
          date: current.date,
          media_url: current.media_url.trim(),
          images: current.images,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error('Error al guardar: ' + (data.detail || 'Error desconocido'));
        return;
      }
      toast.success(modalMode === 'create' ? 'Proyecto creado' : 'Proyecto actualizado');
      setIsModalOpen(false);
      fetchProjects();
    } catch (err) {
      toast.error('Error de conexión con el servidor');
    } finally {
      setSaving(false);
    }
  };

  const deleteProject = async (project) => {
    if (!window.confirm(`¿Seguro que quieres eliminar "${project.title}"?`)) return;
    try {
      const res = await fetch(`${API_BASE_URL}/projects/${project.id}`, { method: 'DELETE' });
      if (!res.ok) {
        toast.error('No se pudo eliminar el proyecto');
        return;
      }
      toast.success('Proyecto eliminado');
      fetchProjects();
    } catch (err) {
      toast.error('Error de conexión con el servidor');
    }
  };

  if (loading) return <div className="p-20 text-center" data-testid="projects-loading">Cargando gestión...</div>;

  return (
    <div className="max-w-6xl mx-auto p-8 pt-28" data-testid="admin-projects-panel">
      <AdminTabs />

      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">Gestor de Proyectos</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="projects-count">
            {filtered.length} de {projects.length} proyectos
          </p>
        </div>
        <button
          onClick={() => openModal('create')}
          data-testid="create-project-button"
          className="bg-primary text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-primary/90 transition-colors"
        >
          <Plus size={18} /> Nuevo Proyecto
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white shadow rounded-lg p-4 mb-6 flex flex-col md:flex-row gap-3" data-testid="projects-filters">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-3 text-slate-400" />
          <input
            className="w-full border rounded pl-9 pr-3 py-2 text-sm"
            placeholder="Buscar por cualquier campo (nombre, tipo, fecha, resumen, enlace...)"
            value={filters.search}
            data-testid="filter-search-input"
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
        <select
          className="border rounded px-3 py-2 text-sm md:w-48"
          value={filters.category}
          data-testid="filter-category-select"
          onChange={(e) => setFilters({ ...filters, category: e.target.value })}
        >
          <option value="">Todos los tipos</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          className="border rounded px-3 py-2 text-sm md:w-36"
          value={filters.year}
          data-testid="filter-year-select"
          onChange={(e) => setFilters({ ...filters, year: e.target.value })}
        >
          <option value="">Todos los años</option>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        {(filters.search || filters.category || filters.year) && (
          <button
            onClick={() => setFilters({ search: '', category: '', year: '' })}
            data-testid="filter-clear-button"
            className="text-sm text-primary hover:underline px-2"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Lista de proyectos */}
      {filtered.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-12 text-center text-muted-foreground" data-testid="projects-empty">
          No hay proyectos que coincidan con los filtros.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-list">
          {filtered.map((project) => (
            <div key={project.id} className="bg-white shadow rounded-lg overflow-hidden flex flex-col" data-testid={`project-card-${project.id}`}>
              <div className="h-36 bg-slate-100 overflow-hidden">
                {project.images?.[0] ? (
                  <img src={project.images[0]} alt={project.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-300">
                    <ImagePlus size={32} />
                  </div>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-bold leading-snug">{project.title}</h3>
                <div className="flex flex-wrap gap-2 mt-2 text-xs">
                  <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full font-semibold">{project.category}</span>
                  {project.date && <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{project.date}</span>}
                  <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{project.images?.length || 0} img</span>
                </div>
                <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
                  <button
                    onClick={() => openModal('edit', project)}
                    data-testid={`edit-project-${project.id}`}
                    className="text-blue-600 hover:text-blue-800 p-1"
                    title="Editar"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => deleteProject(project)}
                    data-testid={`delete-project-${project.id}`}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Eliminar"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal crear/editar */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="project-form-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative p-6">
            <button
              onClick={() => setIsModalOpen(false)}
              data-testid="project-form-close"
              className="absolute top-4 right-4 text-gray-500 hover:text-black"
            >
              <X size={24} />
            </button>

            <h2 className="text-xl font-bold mb-5">
              {modalMode === 'create' ? 'Nuevo Proyecto' : 'Editar Proyecto'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1">Nombre del proyecto *</label>
                <input
                  className="w-full border p-2 rounded text-sm"
                  value={current.title}
                  data-testid="project-title-input"
                  onChange={(e) => setCurrent({ ...current, title: e.target.value })}
                  placeholder="Ej: Mazo de cables para planta fotovoltaica"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">Tipo de proyecto *</label>
                  <input
                    className="w-full border p-2 rounded text-sm"
                    list="project-categories"
                    value={current.category}
                    data-testid="project-category-input"
                    onChange={(e) => setCurrent({ ...current, category: e.target.value })}
                    placeholder="Ej: Mazos de cables"
                  />
                  <datalist id="project-categories">
                    {categories.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">Fecha de finalización *</label>
                  <input
                    type="date"
                    className="w-full border p-2 rounded text-sm"
                    value={current.date}
                    data-testid="project-date-input"
                    onChange={(e) => setCurrent({ ...current, date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">Resumen *</label>
                <textarea
                  className="w-full border p-2 rounded text-sm"
                  rows={4}
                  value={current.description}
                  data-testid="project-description-input"
                  onChange={(e) => setCurrent({ ...current, description: e.target.value })}
                  placeholder="Descripción del proyecto, alcance del trabajo realizado..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-1">Enlace a la web (opcional)</label>
                <input
                  type="url"
                  className="w-full border p-2 rounded text-sm"
                  value={current.media_url}
                  data-testid="project-url-input"
                  onChange={(e) => setCurrent({ ...current, media_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold">
                    Imágenes ({current.images.length}/{MAX_IMAGES})
                  </label>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={processingImages || current.images.length >= MAX_IMAGES}
                    data-testid="project-add-images-button"
                    className="inline-flex items-center gap-2 text-sm bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded disabled:opacity-50"
                  >
                    {processingImages ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
                    {processingImages ? 'Procesando...' : 'Añadir imágenes'}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    data-testid="project-images-input"
                    onChange={handleImagesSelected}
                  />
                </div>
                {current.images.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="project-images-preview">
                    {current.images.map((img, i) => (
                      <div key={i} className="relative group aspect-square rounded overflow-hidden border">
                        <img src={img} alt={`Imagen ${i + 1}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeImage(i)}
                          data-testid={`project-image-remove-${i}`}
                          className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  Las imágenes se optimizan automáticamente antes de guardarse.
                </p>
              </div>

              <button
                onClick={handleSave}
                disabled={saving || processingImages}
                data-testid="project-save-button"
                className="w-full py-2.5 bg-primary text-white rounded font-bold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {saving && <Loader2 size={16} className="animate-spin" />}
                {saving ? 'Guardando...' : 'Guardar Proyecto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjectsPanel;
